import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { throttle } from 'lodash';
import moment from 'moment-timezone';
import { BsCalendar2, BsListCheck } from 'react-icons/bs';
import { IoMdCheckmarkCircleOutline } from 'react-icons/io';
import Loader from '../../../core/components/Loader';
import { FilterSection } from '../../../core/components/FilterSection';
import { Drawer, DrawerContainer } from '../../../core/components/Drawer';
import { EmployeeDropdownAndAddButton } from '../../../core/components/EmployeeDropdownAndAddButton';
import GenericFilterBar, { FilterItem } from '../../../core/components/FilterBar';
import { Calendar, CalendarEvent, CalendarViewTypes } from '../../../core/components/Calendar';
import { selectSiteTimeZone } from '../../settings/reducer';
import { fetchEmployees } from '../../employees/actions';
import { selectEmployees } from '../../employees/reducer';
import { selectTaskLocations } from '../../tasks/reducer';
import { getTaskLocations } from '../../tasks/actions';
import { selectSitesBasic } from '../../site-selection/reducer';
import { fetchSites } from '../../../core/actions';
import { TASK_DRAWER_WIDTH } from '../../tasks/types';
import {
  TaskOccurrence,
  TASK_TEMPLATE_STATUSES,
  useGetTaskOccurrencesQuery,
  useGetTaskTemplateQuery,
} from '../../../core/api/rtk/taskTemplatesApi';
import {
  buildMaintenanceOccurrenceQuery,
  getOccurrenceDoneMoment,
  getOccurrenceDueDate,
  getOccurrenceDueMoment,
  isOccurrenceOverdue,
} from '../helpers';
import TaskOccurrenceCard from '../components/TaskOccurrenceCard';
import { TaskDetails } from '../components/TaskDetails';
import { TaskType } from '../../tasks/models';

const INTERVAL_OPTIONS = ['Hourly', 'Daily', 'Weekly', 'Monthly', 'Yearly', 'CarCount', 'Once'];

const toDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const formatDay = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getDaySuffix = (day: number): string => {
  if (day % 100 >= 11 && day % 100 <= 13) return 'th';
  if (day % 10 === 1) return 'st';
  if (day % 10 === 2) return 'nd';
  if (day % 10 === 3) return 'rd';
  return 'th';
};

const formatLongDate = (date: Date): string => {
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  return `${month} ${day}${getDaySuffix(day)} ${date.getFullYear()}`;
};

const formatRangeLabel = (start: Date, end: Date): string => {
  const sd = start.getDate();
  const ed = end.getDate();
  const sm = start.toLocaleDateString('en-US', { month: 'long' });
  const em = end.toLocaleDateString('en-US', { month: 'long' });
  const sy = start.getFullYear();
  const ey = end.getFullYear();
  if (sy === ey && sm === em) {
    return `${sm} ${sd}${getDaySuffix(sd)}-${ed}${getDaySuffix(ed)}, ${sy}`;
  }
  if (sy === ey) return `${sm} ${sd}${getDaySuffix(sd)} - ${em} ${ed}${getDaySuffix(ed)}, ${sy}`;
  return `${sm} ${sd}${getDaySuffix(sd)}, ${sy} - ${em} ${ed}${getDaySuffix(ed)}, ${ey}`;
};

const getDateRange = (start: Date, end: Date): Date[] => {
  const s = toDay(start);
  const e = toDay(end);
  const range: Date[] = [];
  const step = s <= e ? 1 : -1;
  const current = new Date(s);
  while (step === 1 ? current <= e : current >= e) {
    range.push(new Date(current));
    current.setDate(current.getDate() + step);
  }
  return range;
};

interface FilterBarValues {
  nameQuery: string;
  locationFilter: string[];
  dueDateStart: string;
  dueDateEnd: string;
  statusFilter: string[];
  employeeFilter: string[];
  intervalFilter: string[];
}

const emptyFilterValues = (): FilterBarValues => ({
  nameQuery: '',
  locationFilter: [],
  dueDateStart: '',
  dueDateEnd: '',
  statusFilter: [],
  employeeFilter: [],
  intervalFilter: [],
});

const TasksV2Maintenance = (): JSX.Element => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [viewTab, setViewTab] = useState<'checklist' | 'calendar' | 'completed'>('checklist');
  const [selectedOccurrence, setSelectedOccurrence] = useState<TaskOccurrence | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [filterBarValues, setFilterBarValues] = useState<FilterBarValues>(emptyFilterValues());

  const [selectedDates, setSelectedDates] = useState<Date[]>(() => [toDay(new Date())]);
  const [rangeAnchorDate, setRangeAnchorDate] = useState<Date | null>(() => toDay(new Date()));
  const [collapsedDateGroups, setCollapsedDateGroups] = useState<Record<string, boolean>>({});
  const [defaultCalendarView, setDefaultCalendarView] = useState<CalendarViewTypes>(
    CalendarViewTypes.Monthly
  );

  const employees = useSelector(selectEmployees);
  const locations = useSelector(selectTaskLocations);
  const sites = useSelector(selectSitesBasic);
  const timeZone = useSelector(selectSiteTimeZone);

  useEffect(() => {
    dispatch(fetchEmployees());
  }, [dispatch]);

  useEffect(() => {
    if (!locations.length) dispatch(getTaskLocations());
  }, [dispatch, locations.length]);

  useEffect(() => {
    if (!sites.length) dispatch(fetchSites());
  }, [dispatch, sites.length]);

  useEffect(() => {
    const handleResize = throttle(() => {
      setDefaultCalendarView(
        window.innerWidth < 1001 ? CalendarViewTypes.BiWeekly : CalendarViewTypes.Monthly
      );
    }, 1000);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Wide query + client-side filter; the v2 endpoint only supports single-value selects.
  const queryArgs = useMemo(() => buildMaintenanceOccurrenceQuery(), []);
  const { data, isFetching } = useGetTaskOccurrencesQuery(queryArgs);
  const selectedTaskId = selectedOccurrence?.taskId ?? null;
  const { data: selectedTemplate } = useGetTaskTemplateQuery(selectedTaskId as number, {
    skip: !selectedTaskId,
  });

  const occurrences: TaskOccurrence[] = data?.results || [];

  const matchesFilterBar = (occurrence: TaskOccurrence): boolean => {
    const name = (occurrence.task?.name || occurrence.name || '').toLowerCase();
    const nameQuery = (filterBarValues.nameQuery || '').trim().toLowerCase();
    if (nameQuery && !name.includes(nameQuery)) return false;

    const locationIds = filterBarValues.locationFilter || [];
    if (locationIds.length) {
      const locId = (occurrence.task as any)?.locationId;
      if (locId == null || !locationIds.includes(String(locId))) return false;
    }

    const statuses = filterBarValues.statusFilter || [];
    if (statuses.length && !statuses.includes(String(occurrence.status))) return false;

    const employeeIds = filterBarValues.employeeFilter || [];
    const occEmployeeId = occurrence.employeeId ?? (occurrence.task as any)?.employeeId;
    if (employeeIds.length) {
      if (occEmployeeId == null || !employeeIds.includes(String(occEmployeeId))) return false;
    }

    const intervals = filterBarValues.intervalFilter || [];
    if (intervals.length && !intervals.includes(String(occurrence.task?.interval))) return false;

    const dueStart = filterBarValues.dueDateStart;
    const dueEnd = filterBarValues.dueDateEnd;
    if (dueStart || dueEnd) {
      const taskDate = getOccurrenceDueDate(occurrence);
      if (dueStart && taskDate < new Date(dueStart)) return false;
      if (dueEnd) {
        const endOfDay = new Date(dueEnd);
        endOfDay.setHours(23, 59, 59, 999);
        if (taskDate > endOfDay) return false;
      }
    }
    return true;
  };

  const filteredOccurrences = useMemo(
    () => occurrences.filter(matchesFilterBar),
    [occurrences, filterBarValues]
  );

  const today = timeZone ? moment().tz(timeZone).startOf('day') : moment().startOf('day');
  const tomorrow = today.clone().add(1, 'day');
  const startOfWeek = today.clone().startOf('week');
  const endOfWeek = today.clone().endOf('week');
  const startOfNextWeek = today.clone().add(1, 'week').startOf('week');
  const endOfNextWeek = today.clone().add(1, 'week').endOf('week');

  const incompleteList = filteredOccurrences.filter((o) => o.status !== 'Completed');
  const overdueOccurrences = incompleteList.filter(isOccurrenceOverdue);
  const todayOccurrences = incompleteList.filter((o) => {
    const due = getOccurrenceDueMoment(o);
    return due ? due.isSame(today, 'day') : false;
  });
  const tomorrowOccurrences = incompleteList.filter((o) => {
    const due = getOccurrenceDueMoment(o);
    return due ? due.isSame(tomorrow, 'day') : false;
  });
  const thisWeekOccurrences = incompleteList.filter((o) => {
    const due = getOccurrenceDueMoment(o);
    return due ? due.isAfter(tomorrow, 'day') && due.isSameOrBefore(endOfWeek, 'day') : false;
  });
  const nextWeekOccurrences = incompleteList.filter((o) => {
    const due = getOccurrenceDueMoment(o);
    return due
      ? due.isSameOrAfter(startOfNextWeek, 'day') && due.isSameOrBefore(endOfNextWeek, 'day')
      : false;
  });
  const laterOccurrences = incompleteList.filter((o) => {
    const due = getOccurrenceDueMoment(o);
    if (!due) return true;
    return due.isAfter(endOfNextWeek, 'day');
  });

  const completedToday = filteredOccurrences.filter((o) => {
    const done = getOccurrenceDoneMoment(o);
    return done ? done.isSame(today, 'day') : false;
  });
  const completedThisWeek = filteredOccurrences.filter((o) => {
    const done = getOccurrenceDoneMoment(o);
    return done ? done.isAfter(startOfWeek) && done.isBefore(today) : false;
  });
  const completedPast = filteredOccurrences.filter((o) => {
    const done = getOccurrenceDoneMoment(o);
    return done ? done.isBefore(startOfWeek) : false;
  });

  const calendarEvents = useMemo(
    () =>
      filteredOccurrences.map((o) => ({
        date: toDay(getOccurrenceDueDate(o)),
        data: o,
      })),
    [filteredOccurrences]
  );

  const filteredCalendarOccurrences = useMemo(() => {
    if (!selectedDates.length) return filteredOccurrences;
    return filteredOccurrences.filter((o) => {
      const occDate = toDay(getOccurrenceDueDate(o));
      return selectedDates.some((d) => isSameDay(d, occDate));
    });
  }, [filteredOccurrences, selectedDates]);

  const selectedDateLabel = useMemo(() => {
    if (!selectedDates.length) return '';
    const sorted = [...selectedDates].sort((a, b) => a.getTime() - b.getTime());
    const start = sorted[0];
    const end = sorted[sorted.length - 1];
    if (sorted.length === 1 || isSameDay(start, end)) return formatLongDate(start);
    return formatRangeLabel(start, end);
  }, [selectedDates]);

  const calendarOverdue = useMemo(
    () => filteredOccurrences.filter(isOccurrenceOverdue),
    [filteredOccurrences]
  );
  const calendarNonOverdue = useMemo(
    () => filteredOccurrences.filter((o) => !isOccurrenceOverdue(o)),
    [filteredOccurrences]
  );

  const handleAddMaintenance = (): void => {
    setSelectedOccurrence(null);
    setIsAddingNew(true);
  };

  const handleCloseDrawer = (): void => {
    setSelectedOccurrence(null);
    setIsAddingNew(false);
  };

  const toggleSelectedDate = (date: Date): void => {
    const day = toDay(date);
    const todayDate = toDay(new Date());
    setSelectedDates((prev) => {
      const wasSelected = prev.some((s) => isSameDay(s, day));
      if (wasSelected) {
        if (prev.length > 1) {
          setRangeAnchorDate(day);
          return [day];
        }
        const next = prev.filter((s) => !isSameDay(s, day));
        if (!next.length) {
          setRangeAnchorDate(todayDate);
          return [todayDate];
        }
        if (rangeAnchorDate && isSameDay(rangeAnchorDate, day)) setRangeAnchorDate(next[0]);
        return next;
      }
      if (!rangeAnchorDate || day < rangeAnchorDate) {
        setRangeAnchorDate(day);
        return [day];
      }
      if (isSameDay(rangeAnchorDate, day)) return prev;
      const newRange = getDateRange(rangeAnchorDate, day);
      if (newRange.length > 30) {
        setRangeAnchorDate(day);
        return [day];
      }
      return newRange;
    });
  };

  const selectDateRange = (dates: Date[]): void => {
    setSelectedDates(dates.map(toDay));
    if (dates.length) setRangeAnchorDate(toDay(dates[dates.length - 1]));
  };

  const clearSelectedDates = (): void => {
    const todayDate = toDay(new Date());
    setSelectedDates([todayDate]);
    setRangeAnchorDate(todayDate);
    setCollapsedDateGroups({});
  };

  const renderOccurrence = (occurrence: TaskOccurrence): JSX.Element => {
    const taskData = occurrence.task as any;
    const location = locations.find(
      (loc: any) => String(loc.type) === String(taskData?.locationId)
    );
    const siteData = sites.find((s: any) => String(s.id) === String(taskData?.siteId));
    const locationLabel = location?.message;
    const locationSiteLabel =
      locationLabel && siteData?.name ? `${locationLabel} - ${siteData.name}` : locationLabel;
    const due = getOccurrenceDueMoment(occurrence);
    const overdue = isOccurrenceOverdue(occurrence);
    const dueLabel = due ? due.format('MM/DD/YYYY') : '';
    const additionalLines = (
      <div
        className={`maintenance-item-time maintenance-item-time--stacked${
          overdue ? ' overdue' : ''
        }`}
      >
        {dueLabel && <span className="maintenance-item-time-label">{dueLabel}</span>}
        {locationSiteLabel && (
          <div className="maintenance-item-location-row">
            <span className="maintenance-item-location">{locationSiteLabel}</span>
          </div>
        )}
      </div>
    );
    return (
      <div className="maintenance-item" key={occurrence.id}>
        <TaskOccurrenceCard
          occurrence={occurrence}
          onOpen={setSelectedOccurrence}
          className="tabular maintenance-task-item"
          additionalLines={additionalLines}
        />
      </div>
    );
  };

  const renderCalendarEvent = useMemo(() => {
    return (e: CalendarEvent) => <span data-name={e.data?.task?.name} />;
  }, []);

  const filterBarItems: FilterItem[] = [
    {
      key: 'nameQuery',
      type: 'search',
      label: intl.formatMessage({ id: 'search' }),
      placeholder: intl.formatMessage({ id: 'searchTasks', defaultMessage: 'Search tasks...' }),
      minWidth: 180,
      maxWidth: 280,
    },
    {
      key: 'locationFilter',
      type: 'select',
      label: intl.formatMessage({ id: 'taskLocation', defaultMessage: 'Location' }),
      placeholder: intl.formatMessage({ id: 'all', defaultMessage: 'All' }),
      multi: true,
      options: (locations || []).map((loc: any) => ({
        value: String(loc.type),
        label: loc.message,
      })),
      valueMapper: (arr: any[]) =>
        (arr || []).map((v) => {
          const item = (locations || []).find((loc: any) => String(loc.type) === v);
          return { value: String(v), label: item ? item.message : String(v) };
        }),
    },
    ...(viewTab === 'checklist'
      ? [
          {
            key: 'dueDateRange',
            type: 'dateRange',
            label: intl.formatMessage({
              id: 'estimatedDueDate',
              defaultMessage: 'Estimated Due Date',
            }),
            startKey: 'dueDateStart',
            endKey: 'dueDateEnd',
            startPlaceholder: intl.formatMessage({ id: 'startDate', defaultMessage: 'Start' }),
            endPlaceholder: intl.formatMessage({ id: 'endDate', defaultMessage: 'End' }),
          } as FilterItem,
        ]
      : []),
    {
      key: 'statusFilter',
      type: 'select',
      label: intl.formatMessage({ id: 'taskStatus', defaultMessage: 'Status' }),
      placeholder: intl.formatMessage({ id: 'all', defaultMessage: 'All' }),
      multi: true,
      options: TASK_TEMPLATE_STATUSES.map((s) => ({
        value: s,
        label: s === 'Snoozed' ? 'Blocked' : s,
      })),
      valueMapper: (arr: any[]) =>
        (arr || []).map((v) => ({
          value: String(v),
          label: String(v) === 'Snoozed' ? 'Blocked' : String(v),
        })),
    },
    {
      key: 'employeeFilter',
      type: 'select',
      label: intl.formatMessage({ id: 'filterByTeamMember', defaultMessage: 'Team Member' }),
      placeholder: intl.formatMessage({ id: 'all', defaultMessage: 'All' }),
      multi: true,
      options: (employees || []).map((e: any) => ({
        value: String(e.id),
        label: `${e.lastName}, ${e.firstName}`,
      })),
      valueMapper: (arr: any[]) =>
        (arr || []).map((v) => {
          const emp = (employees || []).find((e: any) => String(e.id) === String(v));
          return {
            value: String(v),
            label: emp ? `${emp.lastName}, ${emp.firstName}` : String(v),
          };
        }),
    },
    {
      key: 'intervalFilter',
      type: 'select',
      label: intl.formatMessage({ id: 'interval', defaultMessage: 'Interval' }),
      placeholder: intl.formatMessage({ id: 'all', defaultMessage: 'All' }),
      multi: true,
      options: INTERVAL_OPTIONS.map((key) => ({
        value: key,
        label: key === 'CarCount' ? 'Car Count' : key,
      })),
      valueMapper: (arr: any[]) =>
        (arr || []).map((v) => ({
          value: String(v),
          label: String(v) === 'CarCount' ? 'Car Count' : String(v),
        })),
    },
  ];

  const sortByName = (a: TaskOccurrence, b: TaskOccurrence): number =>
    (a.task?.name ?? a.name ?? '').localeCompare(b.task?.name ?? b.name ?? '');

  const sortByCompleted = (a: TaskOccurrence, b: TaskOccurrence): number => {
    const ta = new Date(a.completedAt ?? a.timestamp ?? '').getTime();
    const tb = new Date(b.completedAt ?? b.timestamp ?? '').getTime();
    return tb - ta;
  };

  const isTaskIncompleteToday = (o: TaskOccurrence): boolean => {
    const todayMs = new Date().setHours(0, 0, 0, 0);
    const occMs = new Date(o.scheduleEnd || o.scheduleStart || '').setHours(0, 0, 0, 0);
    return o.status !== 'Completed' && occMs <= todayMs;
  };
  const isTaskIncompleteLater = (o: TaskOccurrence): boolean => {
    const todayMs = new Date().setHours(0, 0, 0, 0);
    const occMs = new Date(o.scheduleEnd || o.scheduleStart || '').setHours(0, 0, 0, 0);
    return o.status !== 'Completed' && occMs > todayMs;
  };
  const isTaskComplete = (o: TaskOccurrence): boolean => o.status === 'Completed';

  return (
    <div className="maintenance-tasks-container">
      {/* Tabs + Employee selector */}
      <div className="tasks-filters">
        <div className="task-tabs">
          <button
            type="button"
            className={`button ${viewTab === 'checklist' ? 'active' : ''}`}
            onClick={() => setViewTab('checklist')}
          >
            <BsListCheck />
            {intl.formatMessage({ id: 'checklist', defaultMessage: 'Checklist' })}
          </button>
          <button
            type="button"
            className={`button ${viewTab === 'calendar' ? 'active' : ''}`}
            onClick={() => setViewTab('calendar')}
          >
            <BsCalendar2 />
            {intl.formatMessage({ id: 'calendar', defaultMessage: 'Calendar' })}
          </button>
          <button
            type="button"
            className={`button ${viewTab === 'completed' ? 'active' : ''}`}
            onClick={() => setViewTab('completed')}
          >
            <IoMdCheckmarkCircleOutline />
            {intl.formatMessage({ id: 'completed', defaultMessage: 'Completed' })}
          </button>
        </div>
        <div className="task-filter">
          <EmployeeDropdownAndAddButton onAddFunction={handleAddMaintenance} page="maintenance" />
        </div>
      </div>

      <div className="maintenance-filter-bar">
        <GenericFilterBar
          intl={intl as any}
          values={filterBarValues}
          collapsible
          onChange={(vals: Record<string, any>) =>
            setFilterBarValues((prev) => ({ ...prev, ...vals }))
          }
          onApply={(vals: Record<string, any>) =>
            setFilterBarValues((prev) => ({ ...prev, ...vals }))
          }
          onReset={() => setFilterBarValues(emptyFilterValues())}
          items={filterBarItems}
        />
      </div>

      {viewTab === 'checklist' && (
        <div className="drawer-container">
          <div className="maintenace-list">
            {isFetching ? (
              <Loader />
            ) : (
              <>
                <div className="maintenance-details-header">
                  <span>{intl.formatMessage({ id: 'dueIn' })}</span>
                  <span>{intl.formatMessage({ id: 'interval' })}</span>
                  <span>{intl.formatMessage({ id: 'dueInDays' })}</span>
                </div>
                <div className="tasks-daily-table">
                  {overdueOccurrences.length > 0 && (
                    <div className="maintenance-overdue-rollup">
                      <FilterSection
                        itemList={overdueOccurrences}
                        renderFunction={renderOccurrence}
                        title={intl.formatMessage({ id: 'overdue', defaultMessage: 'Overdue' })}
                        isOpen
                      />
                    </div>
                  )}
                  <FilterSection
                    itemList={todayOccurrences}
                    renderFunction={renderOccurrence}
                    title={intl.formatMessage({ id: 'today', defaultMessage: 'Today' })}
                  />
                  <FilterSection
                    itemList={tomorrowOccurrences}
                    renderFunction={renderOccurrence}
                    title={intl.formatMessage({ id: 'tomorrow', defaultMessage: 'Tomorrow' })}
                  />
                  <FilterSection
                    itemList={thisWeekOccurrences}
                    renderFunction={renderOccurrence}
                    title={intl.formatMessage({ id: 'thisWeek', defaultMessage: 'This Week' })}
                    isOpen={false}
                  />
                  <FilterSection
                    itemList={nextWeekOccurrences}
                    renderFunction={renderOccurrence}
                    title={intl.formatMessage({ id: 'nextWeek', defaultMessage: 'Next Week' })}
                    isOpen={false}
                  />
                  <FilterSection
                    itemList={laterOccurrences}
                    renderFunction={renderOccurrence}
                    title={intl.formatMessage({ id: 'later', defaultMessage: 'Later' })}
                    isOpen={false}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {viewTab === 'completed' && (
        <div className="drawer-container">
          <div className="maintenace-list">
            {isFetching ? (
              <Loader />
            ) : (
              <div className="tasks-daily-table">
                <FilterSection
                  itemList={completedToday}
                  renderFunction={renderOccurrence}
                  title={intl.formatMessage({ id: 'today', defaultMessage: 'Today' })}
                />
                <FilterSection
                  itemList={completedThisWeek}
                  renderFunction={renderOccurrence}
                  title={intl.formatMessage({ id: 'thisWeek', defaultMessage: 'This Week' })}
                  isOpen={false}
                />
                <FilterSection
                  itemList={completedPast}
                  renderFunction={renderOccurrence}
                  title={intl.formatMessage({ id: 'last30', defaultMessage: 'Last 30 Days' })}
                  isOpen={false}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {viewTab === 'calendar' && (
        <div className="maintenance-calendar-drawer-container">
          <DrawerContainer>
            <Drawer expanded expandedWidth={66}>
              <Calendar
                events={calendarEvents}
                renderEvent={renderCalendarEvent}
                selectedDates={selectedDates}
                onSelectDate={toggleSelectedDate}
                onSelectRange={selectDateRange}
                initialView={defaultCalendarView}
              />
            </Drawer>
            <div className="maintenace-list">
              <div
                className={`tasks-daily-table${
                  selectedDates.length > 0 ? ' tasks-daily-table--filtered' : ''
                }`}
              >
                {selectedDateLabel && (
                  <div className="tasks-date-range">
                    <span>{selectedDateLabel}</span>
                    {!(
                      selectedDates.length === 1 && isSameDay(selectedDates[0], toDay(new Date()))
                    ) && (
                      <button
                        className="tasks-date-range-clear"
                        type="button"
                        onClick={clearSelectedDates}
                        aria-label={intl.formatMessage({ id: 'clear', defaultMessage: 'Clear' })}
                      >
                        <i className="uil uil-times" />
                      </button>
                    )}
                  </div>
                )}
                {selectedDates.length > 0 ? (
                  selectedDates
                    .slice()
                    .filter((date) =>
                      filteredCalendarOccurrences.some((o) =>
                        isSameDay(toDay(getOccurrenceDueDate(o)), toDay(date))
                      )
                    )
                    .sort((a, b) => a.getTime() - b.getTime())
                    .map((date, iterator) => {
                      const occurrencesForDate = filteredCalendarOccurrences
                        .filter((o) => isSameDay(toDay(getOccurrenceDueDate(o)), toDay(date)))
                        .sort(sortByName);
                      if (!occurrencesForDate.length) return null;
                      const label = date.toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      });
                      const formattedLabel = formatLongDate(new Date(label));
                      const isCollapsed =
                        label in collapsedDateGroups
                          ? Boolean(collapsedDateGroups[label])
                          : iterator !== 0;
                      return (
                        <div className="tasks-date-group" key={label}>
                          <button
                            className="tasks-date-group-title"
                            type="button"
                            onClick={() =>
                              setCollapsedDateGroups((prev) => ({
                                ...prev,
                                [label]: !isCollapsed,
                              }))
                            }
                          >
                            <span>{formattedLabel}</span>
                            <span className="tasks-date-group-count">
                              {occurrencesForDate.length}
                            </span>
                            <i
                              className={`uil uil-angle-${isCollapsed ? 'down' : 'up'}`}
                              aria-hidden="true"
                            />
                          </button>
                          {!isCollapsed && occurrencesForDate.map((o) => renderOccurrence(o))}
                        </div>
                      );
                    })
                ) : (
                  <>
                    {calendarOverdue.length > 0 && (
                      <div className="maintenance-overdue-rollup">
                        <FilterSection
                          itemList={calendarOverdue}
                          renderFunction={renderOccurrence}
                          title={intl.formatMessage({
                            id: 'overdue',
                            defaultMessage: 'Overdue',
                          })}
                          isOpen
                        />
                      </div>
                    )}
                    <FilterSection
                      itemList={calendarNonOverdue}
                      renderFunction={renderOccurrence}
                      filterFunction={isTaskIncompleteToday}
                      sortFunction={sortByName}
                      title={intl.formatMessage({ id: 'dueToday', defaultMessage: 'Due Today' })}
                    />
                    <FilterSection
                      itemList={calendarNonOverdue}
                      renderFunction={renderOccurrence}
                      filterFunction={isTaskIncompleteLater}
                      sortFunction={sortByName}
                      title={intl.formatMessage({ id: 'dueLater', defaultMessage: 'Due Later' })}
                      isOpen={false}
                    />
                    {filteredOccurrences.some((o) => o.status === 'Completed') && (
                      <FilterSection
                        itemList={calendarNonOverdue}
                        renderFunction={renderOccurrence}
                        filterFunction={isTaskComplete}
                        sortFunction={sortByCompleted}
                        title={intl.formatMessage({
                          id: 'completed',
                          defaultMessage: 'Completed',
                        })}
                        isOpen={false}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </DrawerContainer>
        </div>
      )}

      <Drawer
        expanded={!!selectedOccurrence || isAddingNew}
        maximized={false}
        expandedWidth={TASK_DRAWER_WIDTH}
        overlay
        onBackdropClick={handleCloseDrawer}
      >
        {isAddingNew ? (
          <TaskDetails
            isNew
            type={TaskType.Maintenance}
            onClose={handleCloseDrawer}
            onSave={handleCloseDrawer}
          />
        ) : selectedOccurrence ? (
          <TaskDetails
            template={selectedTemplate ?? null}
            onClose={handleCloseDrawer}
            onSave={handleCloseDrawer}
          />
        ) : null}
      </Drawer>
    </div>
  );
};

export default TasksV2Maintenance;
